import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { config } from './config/wagmi';
import './config/web3modal';
import './App.css';
import './SwapModern.css';

import Header from './components/Header';
import NavBar from './components/NavBar';
import ToastContainer from './components/Toast/ToastContainer';
import { ToastProvider, useToast } from './contexts/ToastContext';
import Dashboard from './pages/Dashboard';
import Swap from './pages/Swap';
import Staking from './pages/Staking';
import Space from './pages/Space';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10000, // 10 seconds - data considered fresh
      cacheTime: 300000, // 5 minutes - keep in cache
      refetchOnWindowFocus: false, // Prevent refetch on tab switch
      refetchOnReconnect: true, // Refetch on network reconnect
      retry: 2, // Retry failed requests 2 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: 1, // Retry mutations once
    }
  }
});

// Scroll to top on route change with smooth animation
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Smooth scroll to top with instant behavior but fade transition
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  return null;
}

function AppContent() {
  const { toasts, removeToast } = useToast();

  return (
    <>
      <div className="app">
        <Header />
        <NavBar />
        <main className="app-main app-fade-in">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/swap" element={<Swap />} />
            <Route path="/staking" element={<Staking />} />
            <Route path="/space" element={<Space />} />
          </Routes>
        </main>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppContent />
            </BrowserRouter>
          </ToastProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </HelmetProvider>
  );
}

export default App;
